/**
 * FloorFootprintGizmoBinding — TransformGizmoBinding over a floor's
 * footprint rectangle (4 corners, possibly rotated) at a fixed height.
 *
 * 2D transforms only: XY translation, Z rotation and XY scale about the
 * centroid — the gizmo's other handles are whitelisted away. The gizmo frame
 * follows the footprint's own rotation ('object' space), so the axis handles
 * and scale cubes align with the rectangle's sides.
 *
 * The target owns persistence: `apply` mirrors the edit rig's live
 * side-handle flow, `commit` its release flow. No undo snapshot is composed
 * (footprint edits persist through the rig's reactive-property flow, like
 * the side handles).
 */

import * as THREE from 'three';
import type { GizmoAxis, GizmoFrame, GizmoSpace, TransformGizmoBinding } from './TransformGizmoBinding';
import type { Snapshot } from '../../ToolUndoRedoManager';

export interface FloorFootprintGizmoTarget {
  /** Current footprint corners (empty/short = no target). */
  getCorners(): THREE.Vector2[];
  /** Height the gizmo floats at (mid-height of the floor box). */
  getMidZ(): number;
  /** Live-apply new corners (drag in progress). */
  apply(corners: THREE.Vector2[]): void;
  /** The drag finished — persist the current corners. */
  commit(): void;
  /** No footprint side may shrink below this. */
  readonly minExtent: number;
}

const Z_AXIS = new THREE.Vector3(0, 0, 1);

/** 2D-only handle set: XY move, Z rotation (3D ring / 2D view ring), XY scale. */
const ENABLED_HANDLES: ReadonlyArray<string> = ['translation.x', 'translation.y', 'rotation.z', 'rotation.view', 'scale.x', 'scale.y', 'scale.xy'];

function centroid(corners: THREE.Vector2[]): THREE.Vector2 {
  const c = new THREE.Vector2();
  for (const corner of corners) c.add(corner);
  return c.multiplyScalar(1 / corners.length);
}

export class FloorFootprintGizmoBinding implements TransformGizmoBinding {
  private baseline: THREE.Vector2[] | null = null;
  private center = new THREE.Vector2();
  private frameAngle = 0;

  constructor(private readonly target: FloorFootprintGizmoTarget) {}

  getFrame(): GizmoFrame | null {
    const corners = this.target.getCorners();
    if (corners.length < 4) return null;
    const c = centroid(corners);
    const angle = Math.atan2(corners[1].y - corners[0].y, corners[1].x - corners[0].x);
    return {
      position: new THREE.Vector3(c.x, c.y, this.target.getMidZ()),
      quaternion: new THREE.Quaternion().setFromAxisAngle(Z_AXIS, angle),
    };
  }

  getSpace(): GizmoSpace {
    return 'object';
  }

  getEnabledHandles(): ReadonlyArray<string> {
    return ENABLED_HANDLES;
  }

  beginDrag(): void {
    const corners = this.target.getCorners();
    this.baseline = corners.map((c) => c.clone());
    this.center = centroid(corners);
    this.frameAngle = Math.atan2(corners[1].y - corners[0].y, corners[1].x - corners[0].x);
  }

  applyTranslation(worldDelta: THREE.Vector3): void {
    if (!this.baseline) return;
    // XY only — the footprint's height is edited via the face repick.
    this.target.apply(this.baseline.map((c) => new THREE.Vector2(c.x + worldDelta.x, c.y + worldDelta.y)));
  }

  applyRotation(worldAxis: THREE.Vector3, angle: number): void {
    if (!this.baseline) return;
    // rotation.z spins about +Z; rotation.view about the view axis, which in
    // the top-down views is ±Z — the sign keeps the sweep direction honest.
    const signed = angle * (worldAxis.z < 0 ? -1 : 1);
    const cos = Math.cos(signed);
    const sin = Math.sin(signed);
    this.target.apply(
      this.baseline.map((c) => {
        const dx = c.x - this.center.x;
        const dy = c.y - this.center.y;
        return new THREE.Vector2(this.center.x + dx * cos - dy * sin, this.center.y + dx * sin + dy * cos);
      }),
    );
  }

  applyScale(axes: ReadonlyArray<GizmoAxis>, factor: number): void {
    if (!this.baseline) return;
    const scaleX = axes.includes('x');
    const scaleY = axes.includes('y');
    if (!scaleX && !scaleY) return;

    // Clamp so no scaled side shrinks below the minimum extent. Extents are
    // measured in the footprint's own frame (edge lengths of the baseline).
    const width = this.baseline[0].distanceTo(this.baseline[1]);
    const depth = this.baseline[1].distanceTo(this.baseline[2]);
    let minFactor = 0;
    if (scaleX && width > 1e-6) minFactor = Math.max(minFactor, this.target.minExtent / width);
    if (scaleY && depth > 1e-6) minFactor = Math.max(minFactor, this.target.minExtent / depth);
    const f = Math.max(minFactor, factor);

    // Scale the listed axes in the footprint's local frame about the centroid.
    const cos = Math.cos(-this.frameAngle);
    const sin = Math.sin(-this.frameAngle);
    this.target.apply(
      this.baseline.map((c) => {
        const dx = c.x - this.center.x;
        const dy = c.y - this.center.y;
        let lx = dx * cos - dy * sin;
        let ly = dx * sin + dy * cos;
        if (scaleX) lx *= f;
        if (scaleY) ly *= f;
        const wx = lx * cos + ly * sin;
        const wy = -lx * sin + ly * cos;
        return new THREE.Vector2(this.center.x + wx, this.center.y + wy);
      }),
    );
  }

  commitDrag(): Snapshot | null {
    this.baseline = null;
    this.target.commit();
    return null;
  }

  cancelDrag(): void {
    // Sub-threshold drag: restore the baseline in case tiny applies landed.
    if (this.baseline) this.target.apply(this.baseline);
    this.baseline = null;
  }
}
